import type { Locale } from '$lib/i18n/types';
import type { AvailableLanguageTag } from '$paraglide/runtime';
import type {
	HierarchySegment,
	LinkSegments,
	LocalizedHierarchy,
	LocalizedLinkSegments,
} from './types';
import { i18nPath } from '$lib/i18n/routing';

export function getLinkSegmentsFromHierarchy(hierarchy: HierarchySegment[]): LinkSegments {
	return hierarchy.map(hierarchy => ({
		value: hierarchy.value,
		label: hierarchy.label ?? undefined,
	}));
}

export function getLinkSegmentsFromHierarchies(
	hierarchies: LocalizedHierarchy[],
): LocalizedLinkSegments {
	return hierarchies.reduce((acc, el) => {
		acc[el.locale as Locale] = getLinkSegmentsFromHierarchy(el.hierarchy);

		return acc;
	}, {} as LocalizedLinkSegments);
}

export function getLinkSegmentsFromNearbyCities(
	nearbyCitiesHierarchies: HierarchySegment[][],
): LinkSegments[] {
	return nearbyCitiesHierarchies.map(getLinkSegmentsFromHierarchy);
}

/**
 * get label from last link segment
 */
export function getSegmentsLabel(segments: LinkSegments) {
	return segments[segments.length - 1]?.label ?? '';
}

export function getRouteIdentifierFromSegments(segments: LinkSegments): string {
	return segments
		.slice()
		.reverse()
		.map(s => s.value)
		.join('-');
}

export function getItineraryIdentifierRouteFromSegments(
	departureSegments: LinkSegments,
	arrivalSegments: LinkSegments,
): string {
	const departure = getRouteIdentifierFromSegments(departureSegments);
	const arrival = getRouteIdentifierFromSegments(arrivalSegments);

	return `${departure}-to-${arrival}`;
}

export function getItineraryLinkSegmentsFromHierarchies(
	departureHierarchies: LocalizedHierarchy[],
	arrivalHierarchies: LocalizedHierarchy[],
): LocalizedLinkSegments {
	const departureSegments = getLinkSegmentsFromHierarchies(departureHierarchies);
	const arrivalSegments = getLinkSegmentsFromHierarchies(arrivalHierarchies);

	const entries = Object.entries(departureSegments).map(([locale, segments]) => {
		return [
			locale,
			[
				{
					// We can safely cast locale to Locale because it's a key of LocalizedLinkSegments
					value: getItineraryIdentifierRouteFromSegments(
						segments,
						arrivalSegments[locale as Locale],
					),
					label: `${getSegmentsLabel(departureSegments[locale as Locale])} - ${getSegmentsLabel(arrivalSegments[locale as Locale])}`,
				},
			],
		];
	});

	return Object.fromEntries(entries) as LocalizedLinkSegments;
}

/**
 * convert link segments to localized url, prepend prefixes
 */
export function segments2href(
	segments: LinkSegments,
	lang: AvailableLanguageTag,
	prefixes?: string[],
) {
	const urlSegments = segments.map(s => s.value).filter(Boolean);

	if (prefixes) {
		urlSegments.unshift(...prefixes);
	}

	return i18nPath(`/${urlSegments.join('/')}`, lang);
}

/**
 * Convert a link segment to a route URL part
 */
export function segments2route(
	departureSegments: LinkSegments,
	arrivalSegments: LinkSegments,
	lang: AvailableLanguageTag,
) {
	const departure = getRouteIdentifierFromSegments(departureSegments);
	const arrival = getRouteIdentifierFromSegments(arrivalSegments);

	return i18nPath(`/routes/results/${departure}-to-${arrival}`, lang);
}
